import api, { apiWithRetry } from './api';

/**
 * Activities service for fetching activities from Strapi API
 */

/**
 * Build query parameters for Strapi API
 * @param {string|null} activityType - Activity type filter
 * @param {string} locale - Locale string (e.g. 'en', 'vi', 'pl')
 * @param {Object} options - Query options
 * @returns {Object} Query parameters
 */
const buildActivityParams = (activityType, locale, options = {}) => {
  const {
    page = 1,
    pageSize = 10,
    sort = 'publishedAt:desc',
    populate = '*',
  } = options;

  const params = {
    'pagination[page]': page,
    'pagination[pageSize]': pageSize,
    'sort': sort,
    'populate': populate,
  };

  if (locale) {
    params['locale'] = locale;
  }

  if (activityType) {
    params['filters[activityType][$eq]'] = activityType;
  }

  return params;
};

export const activitiesService = {
  /**
   * Get activities filtered by type
   * @param {string} activityType - One of: 'announcement', 'vastp_activity', 'seminar'
   * @param {string} locale - Locale string (e.g. 'en', 'vi', 'pl')
   * @param {Object} options - Query options
   * @param {number} options.page - Page number (default: 1)
   * @param {number} options.pageSize - Items per page (default: 10)
   * @param {string} options.sort - Sort order (default: 'publishedAt:desc')
   * @param {string} options.populate - Relations to populate (default: '*')
   * @returns {Promise<Object>} Activities data with pagination meta
   */
  getActivitiesByType: async (activityType, locale, options = {}) => {
    try {
      const params = buildActivityParams(activityType, locale, options);

      const response = await apiWithRetry(() =>
        api.get('/api/activities', { params })
      );

      return response.data;
    } catch (error) {
      console.error(`Error fetching activities by type "${activityType}":`, error);
      throw new Error(
        error.userMessage || `Failed to fetch activities of type "${activityType}"`
      );
    }
  },

  /**
   * Get all activities (all types)
   * @param {string} locale - Locale string (e.g. 'en', 'vi', 'pl')
   * @param {Object} options - Query options
   * @param {number} options.page - Page number (default: 1)
   * @param {number} options.pageSize - Items per page (default: 10)
   * @param {string} options.sort - Sort order (default: 'publishedAt:desc')
   * @param {string} options.populate - Relations to populate (default: '*')
   * @returns {Promise<Object>} Activities data with pagination meta
   */
  getAllActivities: async (locale, options = {}) => {
    try {
      const params = buildActivityParams(null, locale, options);

      const response = await apiWithRetry(() =>
        api.get('/api/activities', { params })
      );

      return response.data;
    } catch (error) {
      console.error('Error fetching all activities:', error);
      throw new Error(error.userMessage || 'Failed to fetch activities');
    }
  },

  /**
   * Get single activity by slug
   * @param {string} slug - Activity slug
   * @param {string} locale - Locale string (e.g. 'en', 'vi', 'pl')
   * @param {string} populate - Relations to populate (default: '*')
   * @returns {Promise<Object>} Activity data
   */
  getActivityBySlug: async (slug, locale, populate = '*') => {
    try {
      const params = {
        'filters[slug][$eq]': slug,
        'populate': populate,
      };

      if (locale) {
        params['locale'] = locale;
      }

      const response = await apiWithRetry(() =>
        api.get('/api/activities', { params })
      );

      if (response.data.data && response.data.data.length > 0) {
        return response.data.data[0];
      } else {
        throw new Error('Activity not found');
      }
    } catch (error) {
      console.error(`Error fetching activity by slug "${slug}":`, error);
      throw new Error(error.userMessage || 'Failed to fetch activity');
    }
  },
};

export default activitiesService;
